import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Phone, Clock, Shield, RefreshCw, CheckCircle2, AlertCircle, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PhoneSettingsPage() {
  const { token, user } = useAuth();
  const [phone, setPhone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  const fetchPhone = useCallback(async () => {
    try {
      const agentId = user?.id;
      if (!agentId) return;
      const { data } = await axios.get(`${API}/phone/status/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPhone(data);
    } catch (err) {
      console.error('Failed to load phone:', err);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => { fetchPhone(); }, [fetchPhone]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const agentId = user?.id;
      const { data } = await axios.post(`${API}/phone/retry-provision/${agentId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.status === 'assigned') {
        toast.success('Phone number assigned successfully!');
      } else if (data.status === 'reactivated') {
        toast.success('Your existing number is active again');
      } else if (data.status === 'already_active') {
        toast.info('Phone number already active');
      } else {
        toast.info(data.message || 'Provisioning attempted');
      }
      await fetchPhone();
    } catch (err) {
      toast.error('Failed to retry provisioning');
    } finally {
      setRetrying(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await fetchPhone();
    toast.success('Phone status refreshed');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  const isActive = phone?.is_active;
  const status = phone?.assignment_status || 'pending';
  const isLive = phone?.live_provisioning_enabled;
  const events = phone?.recent_events || [];

  const statusConfig = {
    assigned: { color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', label: 'Active', icon: CheckCircle2 },
    pending: { color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', label: 'Pending', icon: Clock },
    pending_test_mode: { color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', label: 'Test Mode', icon: AlertCircle },
    pending_assignment: { color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', label: 'Pending Assignment', icon: Clock },
    failed: { color: 'bg-red-500/10 text-red-400 border-red-500/20', label: 'Failed', icon: AlertCircle },
  };
  const sc = statusConfig[status] || statusConfig.pending;
  const StatusIcon = sc.icon;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto space-y-5 sm:space-y-6" data-testid="phone-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Phone Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your dedicated AI phone line</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} className="border-border text-foreground hover:bg-muted" data-testid="refresh-phone-btn">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>

      {/* Phone number card */}
      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Phone className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-foreground text-base">AI Business Number</CardTitle>
                <CardDescription>Your public-facing dedicated line</CardDescription>
              </div>
            </div>
            <Badge className={`text-xs ${sc.color}`} data-testid="phone-status-badge">
              <StatusIcon size={12} className="mr-1" /> {sc.label}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted rounded-lg p-4">
            <p className="text-xs text-muted-foreground mb-1">Assigned Number</p>
            <p className="text-xl font-mono font-semibold text-foreground" data-testid="phone-number-display">
              {phone?.phone_number || 'Awaiting assignment'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-muted rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-0.5">Forwarding To</p>
              <p className="text-sm font-mono text-foreground" data-testid="forward-to-display">
                {phone?.forward_to_masked || '***-***-****'}
              </p>
            </div>
            <div className="bg-muted rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-0.5">Product</p>
              <p className="text-sm text-foreground">{phone?.product_type || 'real_estate'}</p>
            </div>
          </div>

          {!isActive && (
            <div className="flex items-start gap-3 bg-amber-500/5 border border-amber-500/10 rounded-lg p-4">
              <Clock className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-300">
                  {status === 'pending_test_mode' ? 'Test Mode Active' : 'Number Pending'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {status === 'pending_test_mode'
                    ? 'Live provisioning is currently disabled. Enable it in your environment to assign a real SignalWire number.'
                    : 'Your dedicated phone number will be provisioned via SignalWire. You can retry below or wait for automatic assignment.'}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          {!isActive && (
            <div className="flex gap-2">
              <Button
                onClick={handleRetry}
                disabled={retrying}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="retry-provision-btn"
              >
                {retrying ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
                {retrying ? 'Provisioning...' : 'Retry Number Assignment'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Provisioning config */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base">Provisioning Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-muted-foreground">Live Provisioning</span>
            <Badge className={`text-xs ${isLive ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-accent/50 text-muted-foreground border-border'}`}>
              {isLive ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-muted-foreground">Inbound Calls</span>
            <Badge className={`text-xs ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-accent/50 text-muted-foreground border-border'}`}>
              {isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-muted-foreground">Assignment Status</span>
            <span className="text-sm text-foreground">{status.replace(/_/g, ' ')}</span>
          </div>
        </CardContent>
      </Card>

      {/* Recent provisioning events */}
      {events.length > 0 && (
        <Card className="border-border bg-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-foreground text-base">Recent Events</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {events.map((evt, i) => (
              <div key={i} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
                <div className={`mt-1 h-2 w-2 rounded-full shrink-0 ${evt.event_type === 'success' ? 'bg-emerald-400' : evt.event_type === 'failed' ? 'bg-red-400' : 'bg-amber-400'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">{evt.event_type.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-muted-foreground truncate">{evt.details?.message || evt.details?.error || ''}</p>
                </div>
                <span className="text-xs text-muted-foreground shrink-0">
                  {new Date(evt.created_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* How it works */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-foreground text-base">How It Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="h-7 w-7 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0 text-xs font-bold text-emerald-400">1</div>
            <div>
              <p className="text-sm font-medium text-foreground">Customer calls your AI number</p>
              <p className="text-xs text-muted-foreground">Your AI assistant answers immediately, 24/7</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="h-7 w-7 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0 text-xs font-bold text-emerald-400">2</div>
            <div>
              <p className="text-sm font-medium text-foreground">AI handles the conversation</p>
              <p className="text-xs text-muted-foreground">Answers questions, qualifies leads, takes messages</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="h-7 w-7 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0 text-xs font-bold text-emerald-400">3</div>
            <div>
              <p className="text-sm font-medium text-foreground">Transfer when needed</p>
              <p className="text-xs text-muted-foreground">Caller is transferred to your personal phone behind the scenes</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="h-7 w-7 rounded-full bg-accent/50 flex items-center justify-center shrink-0">
              <Shield className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Your personal number stays private</p>
              <p className="text-xs text-muted-foreground">Callers only see the dedicated AI business line</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
